import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Calendar,
  CalendarDays,
  Check,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Download,
  FileText,
  FolderKanban,
  Package,
  Pencil,
  Pin,
  Send,
  Trash2,
  User as UserIcon,
  X,
  Zap,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { TagBadge } from "@/components/ui/TagBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  useAddNoteComment,
  useCreateNote,
  useDeleteNote,
  useDeleteNoteComment,
  useNote,
  useNoteComments,
  useNoteRelated,
  useNoteStatuses,
  useNoteTags,
  useNoteTypes,
  useTogglePin,
  useUpdateNote,
  useUpdateNoteContent,
} from "../hooks/useNotes";

const BORDER = "hsl(210,30%,85%)";

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function countWords(html: string): number {
  const text = stripHtml(html);
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatRelative(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

const TYPE_COLORS: Record<string, { bg: string; text: string }> = {
  general: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
  meeting_minutes: { bg: "hsl(210,85%,95%)", text: "hsl(210,85%,38%)" },
  technical_doc: { bg: "hsl(270,70%,96%)", text: "hsl(270,60%,42%)" },
  decision_log: { bg: "hsl(180,60%,93%)", text: "hsl(180,70%,28%)" },
  reference: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)" },
  personal: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)" },
};

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  draft: { bg: "hsl(215,15%,94%)", text: "hsl(215,20%,38%)", border: "hsl(215,12%,82%)" },
  published: { bg: "hsl(142,55%,94%)", text: "hsl(142,65%,30%)", border: "hsl(142,45%,78%)" },
  archived: { bg: "hsl(40,95%,94%)", text: "hsl(35,85%,38%)", border: "hsl(40,85%,80%)" },
};

function TypeBadge({ value, label }: { value: string; label: string }) {
  const c = TYPE_COLORS[value] ?? TYPE_COLORS.general;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.text }}
    >
      {label}
    </span>
  );
}

function StatusBadge({ value, label }: { value: string; label: string }) {
  const c = STATUS_COLORS[value] ?? STATUS_COLORS.draft;
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium"
      style={{ background: c.bg, color: c.text, borderColor: c.border }}
    >
      {label}
    </span>
  );
}

function IconBtn({
  icon: Icon,
  label,
  onClick,
  active,
  color,
  disabled,
}: {
  icon: typeof Pin;
  label: string;
  onClick: () => void;
  active?: boolean;
  color?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground disabled:opacity-50"
      style={{ borderColor: BORDER }}
    >
      <Icon
        className="h-[15px] w-[15px]"
        strokeWidth={1.5}
        style={{
          color: active && color ? color : undefined,
          fill: active && color ? color : "none",
        }}
      />
    </button>
  );
}

type Heading = { id: string; level: number; text: string };

function extractHeadings(html: string): Heading[] {
  if (!html) return [];
  const div = document.createElement("div");
  div.innerHTML = html;
  const nodes = Array.from(div.querySelectorAll("h1,h2,h3"));
  return nodes.map((el, i) => ({
    id: `heading-${i}`,
    level: parseInt(el.tagName.substring(1), 10),
    text: el.textContent ?? "",
  }));
}

export default function NoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const noteId = id ?? "";

  const { data: note, isLoading, isError } = useNote(noteId);
  const { data: related } = useNoteRelated(noteId);
  const { data: comments = [] } = useNoteComments(noteId);

  const updateNote = useUpdateNote(noteId);
  const updateContent = useUpdateNoteContent(noteId);
  const togglePin = useTogglePin();
  const deleteNote = useDeleteNote();
  const createNote = useCreateNote();
  const addComment = useAddNoteComment(noteId);
  const deleteComment = useDeleteNoteComment(noteId);

  const noteTypes = useNoteTypes().data ?? [];
  const noteStatuses = useNoteStatuses().data ?? [];
  const noteTagOptions = useNoteTags().data ?? [];

  const [editingMeta, setEditingMeta] = useState(false);
  const [editingContent, setEditingContent] = useState(
    search.get("edit") === "true",
  );
  const [titleDraft, setTitleDraft] = useState("");
  const [typeDraft, setTypeDraft] = useState("general");
  const [statusDraft, setStatusDraft] = useState("draft");
  const [visibilityDraft, setVisibilityDraft] = useState<"team" | "private">("team");
  const [tagsDraft, setTagsDraft] = useState<string[]>([]);
  const [isPinnedDraft, setIsPinnedDraft] = useState(false);
  const [companyIdDraft, setCompanyIdDraft] = useState<string | null>(null);
  const [productIdDraft, setProductIdDraft] = useState<string | null>(null);
  const [projectIdDraft, setProjectIdDraft] = useState<string | null>(null);
  const [meetingIdDraft, setMeetingIdDraft] = useState<string | null>(null);
  const [taskIdDraft, setTaskIdDraft] = useState<string | null>(null);
  const [actionIdDraft, setActionIdDraft] = useState<string | null>(null);
  const [contentDraft, setContentDraft] = useState("");
  const [tab, setTab] = useState<"related" | "comments" | "attachments">(
    "related",
  );
  const [metaExpanded, setMetaExpanded] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [toast, setToast] = useState<string | null>(null);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialContent = useRef<string>("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  useEffect(() => {
    if (note) {
      setTitleDraft(note.title);
      setTypeDraft(note.type);
      setStatusDraft(note.status);
      setVisibilityDraft((note.visibility as "team" | "private") ?? "team");
      setTagsDraft(note.tags ?? []);
      setIsPinnedDraft(note.isPinned);
      setCompanyIdDraft(note.companyId ?? null);
      setProductIdDraft(note.productId ?? null);
      setProjectIdDraft(note.projectId ?? null);
      setMeetingIdDraft(note.meetingId ?? null);
      setTaskIdDraft(note.taskId ?? null);
      setActionIdDraft(note.actionId ?? null);
      setContentDraft(note.content);
      initialContent.current = note.content;
    }
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const wordCount = useMemo(() => countWords(contentDraft), [contentDraft]);
  const readingMinutes = Math.max(1, Math.ceil((note?.wordCount ?? wordCount) / 220));

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  useEffect(() => {
    if (!editingContent || !note) return;
    if (contentDraft === initialContent.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveState("idle");
    saveTimer.current = setTimeout(() => {
      void doContentSave();
    }, 2000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [contentDraft, editingContent]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s" && editingContent) {
        e.preventDefault();
        void doContentSave();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editingContent, contentDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  async function doContentSave() {
    if (!note) return;
    setSaveState("saving");
    try {
      await updateContent.mutateAsync({ content: contentDraft, wordCount });
      initialContent.current = contentDraft;
      setSaveState("saved");
    } catch {
      setSaveState("idle");
    }
  }

  async function handleMetaSave() {
    if (!note) return;
    try {
      await updateNote.mutateAsync({
        title: titleDraft.trim() || note.title,
        type: typeDraft,
        status: statusDraft,
        visibility: visibilityDraft,
        tags: tagsDraft,
        isPinned: isPinnedDraft,
        companyId: companyIdDraft,
        productId: productIdDraft,
        projectId: projectIdDraft,
        meetingId: meetingIdDraft,
        taskId: taskIdDraft,
        actionId: actionIdDraft,
      });
      setEditingMeta(false);
      showToast("Note updated");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save");
    }
  }

  async function handleTogglePin() {
    if (!note) return;
    await togglePin.mutateAsync({ id: note.id, isPinned: !note.isPinned });
    showToast(note.isPinned ? "Unpinned" : "Pinned");
  }

  function handleDelete() {
    if (!note) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!note) return;
    await deleteNote.mutateAsync(note.id);
    navigate("/notes");
  }

  async function handleDuplicate() {
    if (!note) return;
    const res = await createNote.mutateAsync({
      title: `${note.title} (Copy)`,
      content: note.content,
      type: note.type,
      status: "draft",
      tags: note.tags,
      visibility: note.visibility,
      companyId: note.companyId,
      productId: note.productId,
      projectId: note.projectId,
      meetingId: note.meetingId,
      taskId: note.taskId,
      actionId: note.actionId,
    });
    showToast("Note duplicated");
    navigate(`/notes/${res.note.id}`);
  }

  function handleExport() {
    showToast("Export coming soon");
  }

  async function handleAddComment() {
    const text = commentText.trim();
    if (!text) return;
    setCommentText("");
    try {
      await addComment.mutateAsync(text);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to add comment");
    }
  }

  const headings = useMemo(
    () => extractHeadings(editingContent ? contentDraft : note?.content ?? ""),
    [note?.content, contentDraft, editingContent],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={[{ label: "Notes", href: "/notes" }]}>
        <div className="py-16 text-center text-sm text-muted-foreground">Loading note…</div>
      </AppLayout>
    );
  }
  if (isError || !note) {
    return (
      <AppLayout breadcrumb={[{ label: "Notes", href: "/notes" }]}>
        <div className="py-16 text-center text-sm text-destructive">Note not found.</div>
      </AppLayout>
    );
  }

  const typeLabel = noteTypes.find((t) => t.value === note.type)?.label ?? note.type;
  const statusLabel = noteStatuses.find((s) => s.value === note.status)?.label ?? note.status;

  const relatedTotal =
    (related?.tasks.length ?? 0) +
    (related?.actions.length ?? 0) +
    (related?.decisions.length ?? 0) +
    (related?.meetings.length ?? 0);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Notes", href: "/notes" },
        { label: note.code ?? note.title },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/notes")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
              style={{ borderColor: BORDER }}
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <FileText className="h-[18px] w-[18px]" strokeWidth={1.5} />
            <h1 className="text-xl font-semibold">View Note</h1>
          </div>
          <div className="flex items-center gap-1.5">
            <IconBtn
              icon={Pin}
              label={note.isPinned ? "Unpin" : "Pin"}
              onClick={handleTogglePin}
              active={note.isPinned}
              color="hsl(40,95%,55%)"
            />
            <IconBtn icon={Download} label="Export" onClick={handleExport} />
            <IconBtn icon={Copy} label="Duplicate" onClick={handleDuplicate} />
            <IconBtn icon={Clock} label="History" onClick={() => showToast("History coming soon")} />
            <IconBtn icon={Pencil} label="Edit" onClick={() => setEditingMeta((v) => !v)} active={editingMeta} />
            <IconBtn icon={Trash2} label="Delete" onClick={handleDelete} />
          </div>
        </div>

        {/* ROW 1 — Summary header card */}
        <div
          className="rounded-xl border bg-white p-5"
          style={{ borderColor: BORDER }}
        >
          {editingMeta ? (
            <input
              className="w-full text-[20px] font-bold outline-none border-b pb-1"
              style={{ borderColor: BORDER }}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
            />
          ) : (
            <h2 className="text-[20px] font-bold leading-tight">{note.title}</h2>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
            {note.code && <ReferenceIdBadge id={note.code} />}
            {editingMeta ? (
              <>
                <select
                  value={typeDraft}
                  onChange={(e) => setTypeDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {noteTypes.length === 0 ? (
                    <option value="general">General</option>
                  ) : (
                    noteTypes.map((t) => (
                      <option key={t.id} value={t.value}>{t.label}</option>
                    ))
                  )}
                </select>
                <select
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  className="h-7 rounded-md border px-2 text-[12px]"
                  style={{ borderColor: BORDER }}
                >
                  {noteStatuses.length === 0 ? (
                    <>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </>
                  ) : (
                    noteStatuses.map((s) => (
                      <option key={s.id} value={s.value}>{s.label}</option>
                    ))
                  )}
                </select>
                <div className="inline-flex rounded-md border p-0.5" style={{ borderColor: BORDER }}>
                  {(["team", "private"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setVisibilityDraft(v)}
                      className="px-2 py-0.5 text-[11px] font-medium rounded"
                      style={{
                        background: visibilityDraft === v ? "hsl(210,55%,88%)" : "transparent",
                        color: visibilityDraft === v ? "hsl(210,100%,25%)" : "hsl(215,16%,47%)",
                      }}
                    >
                      {v === "team" ? "Team" : "Private"}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <TypeBadge value={note.type} label={typeLabel} />
                <StatusBadge value={note.status} label={statusLabel} />
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                  style={{
                    background: note.visibility === "private" ? "hsl(215,15%,94%)" : "hsl(210,85%,95%)",
                    color: note.visibility === "private" ? "hsl(215,20%,38%)" : "hsl(210,85%,38%)",
                  }}
                >
                  {note.visibility === "private" ? "Private" : "Team"}
                </span>
                {note.isPinned && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium"
                    style={{ background: "hsl(40,95%,94%)", color: "hsl(35,85%,38%)", borderColor: "hsl(40,85%,80%)" }}
                  >
                    <Pin className="h-3 w-3" strokeWidth={1.5} style={{ fill: "hsl(40,95%,55%)" }} />
                    Pinned
                  </span>
                )}
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
              {note.createdByName ?? "—"}
            </span>
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
              {formatDate(note.createdAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
              {readingMinutes} min read
            </span>
          </div>

          {/* Context breadcrumb */}
          <div className="mt-3 flex flex-wrap items-center gap-1 text-[12px] text-muted-foreground">
            {note.companyName && (
              <>
                <Link to={`/companies/${note.companyId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Building2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {note.companyName}
                </Link>
                {note.productName && <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
              </>
            )}
            {note.productName && (
              <>
                <Link to={`/products/${note.productId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {note.productName}
                </Link>
                {note.projectName && <ChevronRight className="h-3 w-3 text-muted-foreground" strokeWidth={1.5} />}
              </>
            )}
            {note.projectName && (
              <Link to={`/projects/${note.projectId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                <FolderKanban className="h-3.5 w-3.5" strokeWidth={1.5} />
                {note.projectName}
              </Link>
            )}
            {note.meetingTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link to={`/meetings/${note.meetingId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {note.meetingTitle}
                </Link>
              </>
            )}
            {note.taskTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link to={`/tasks/${note.taskId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <CheckSquare className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {note.taskCode ?? note.taskTitle}
                </Link>
              </>
            )}
            {note.actionTitle && (
              <>
                <span className="px-1.5">|</span>
                <Link to={`/actions/${note.actionId}`} className="inline-flex items-center gap-1 hover:text-foreground">
                  <Zap className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {note.actionCode ?? note.actionTitle}
                </Link>
              </>
            )}
          </div>

          {/* Tags */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {editingMeta ? (
              noteTagOptions.map((opt) => {
                const selected = tagsDraft.includes(opt.value);
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() =>
                      setTagsDraft((prev) =>
                        prev.includes(opt.value)
                          ? prev.filter((v) => v !== opt.value)
                          : [...prev, opt.value],
                      )
                    }
                    style={{ opacity: selected ? 1 : 0.5 }}
                  >
                    <TagBadge value={opt.value} label={opt.label} size="md" />
                  </button>
                );
              })
            ) : note.tags.length === 0 ? (
              <span className="text-[11px] text-muted-foreground italic">No tags</span>
            ) : (
              note.tags.map((t) => {
                const opt = noteTagOptions.find((o) => o.value === t);
                return <TagBadge key={t} value={t} label={opt?.label ?? t} size="md" />;
              })
            )}
          </div>

          {editingMeta && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: BORDER }}>
              <label className="flex items-center gap-2 text-sm mb-4">
                <input
                  type="checkbox"
                  checked={isPinnedDraft}
                  onChange={(e) => setIsPinnedDraft(e.target.checked)}
                />
                Pinned
              </label>
              <AssociationFields
                companyId={companyIdDraft}
                productId={productIdDraft}
                projectId={projectIdDraft}
                meetingId={meetingIdDraft}
                taskId={taskIdDraft}
                actionId={actionIdDraft}
                showMeeting
                showTask
                showAction
                onChange={(field, value) => {
                  if (field === "companyId") setCompanyIdDraft(value);
                  else if (field === "productId") setProductIdDraft(value);
                  else if (field === "projectId") setProjectIdDraft(value);
                  else if (field === "meetingId") setMeetingIdDraft(value);
                  else if (field === "taskId") setTaskIdDraft(value);
                  else if (field === "actionId") setActionIdDraft(value);
                }}
              />
            </div>
          )}

          {editingMeta && (
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" onClick={handleMetaSave} disabled={updateNote.isPending}>
                <Check className="h-4 w-4" strokeWidth={1.5} />
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingMeta(false);
                  setTitleDraft(note.title);
                  setTypeDraft(note.type);
                  setStatusDraft(note.status);
                  setVisibilityDraft((note.visibility as "team" | "private") ?? "team");
                  setTagsDraft(note.tags);
                  setIsPinnedDraft(note.isPinned);
                  setCompanyIdDraft(note.companyId ?? null);
                  setProductIdDraft(note.productId ?? null);
                  setProjectIdDraft(note.projectId ?? null);
                  setMeetingIdDraft(note.meetingId ?? null);
                  setTaskIdDraft(note.taskId ?? null);
                  setActionIdDraft(note.actionId ?? null);
                }}
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* ROW 2 — Content */}
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: headings.length > 0 ? "1fr 200px" : "1fr" }}
        >
          <div
            className="overflow-hidden rounded-xl border bg-white"
            style={{ borderColor: BORDER }}
          >
            <div
              className="flex items-center justify-between border-b px-5 py-3"
              style={{ borderColor: BORDER }}
            >
              <span className="text-[13px] font-semibold">Content</span>
              <div className="flex items-center gap-3">
                {editingContent && (
                  <>
                    <span className="text-[11px] text-muted-foreground">
                      {wordCount} words
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {saveState === "saving"
                        ? "Saving..."
                        : saveState === "saved"
                          ? "Saved ✓"
                          : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() => void doContentSave()}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-[hsl(142,65%,30%)] hover:bg-[hsl(142,55%,94%)]"
                      title="Save"
                    >
                      <Check className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setContentDraft(note.content);
                        initialContent.current = note.content;
                        setEditingContent(false);
                        setSaveState("idle");
                      }}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-destructive hover:bg-[hsl(0,70%,96%)]"
                      title="Cancel"
                    >
                      <X className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </>
                )}
                {!editingContent && (
                  <>
                    <button
                      type="button"
                      onClick={handleExport}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                      title="Export"
                    >
                      <Download className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingContent(true)}
                      className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="px-6 py-5">
              {editingContent ? (
                <RichTextEditor
                  content={contentDraft}
                  onChange={setContentDraft}
                  toolbarVariant="full"
                  minHeight={400}
                  placeholder="Start writing..."
                />
              ) : note.content ? (
                <div
                  className="rich-text-content prose prose-sm max-w-none"
                  style={{ fontSize: 14, lineHeight: 1.8 }}
                  dangerouslySetInnerHTML={{ __html: note.content }}
                />
              ) : (
                <div className="py-10 text-center text-sm text-muted-foreground italic">
                  No content yet. Click Edit to start writing.
                </div>
              )}
            </div>
          </div>

          {headings.length > 0 && (
            <div
              className="h-fit rounded-xl border bg-white p-4 sticky"
              style={{ borderColor: BORDER, top: 80 }}
            >
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                On this page
              </div>
              <ul className="space-y-1 text-[12px]">
                {headings.map((h) => (
                  <li
                    key={h.id}
                    style={{ paddingLeft: (h.level - 1) * 10 }}
                    className={
                      h.level === 1
                        ? "font-semibold text-foreground"
                        : h.level === 2
                          ? "text-foreground"
                          : "text-muted-foreground"
                    }
                  >
                    <span className="cursor-default truncate block">{h.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ROW 3 — Tabs */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <div
            className="flex items-center gap-1 border-b px-3 pt-2"
            style={{ borderColor: BORDER }}
          >
            {([
              { key: "related", label: "Related Items", count: relatedTotal },
              { key: "comments", label: "Comments", count: comments.length },
              { key: "attachments", label: "Attachments", count: 0 },
            ] as const).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium transition-colors"
                style={{
                  borderBottom:
                    tab === t.key
                      ? "2px solid hsl(210,100%,50%)"
                      : "2px solid transparent",
                  color: tab === t.key ? "hsl(210,100%,28%)" : "hsl(215,16%,47%)",
                }}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-semibold"
                  style={{
                    background: "hsl(210,30%,94%)",
                    color: "hsl(210,30%,40%)",
                  }}
                >
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="p-5">
            {tab === "related" && (
              <div className="space-y-5">
                {related && relatedTotal === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No related items yet. Link tasks, actions, or decisions to this note.
                  </div>
                )}
                {related && related.tasks.length > 0 && (
                  <RelatedSection
                    title="Tasks"
                    columns={["#", "Title", "Status", "Due Date", "Assignee"]}
                    rows={related.tasks.map((t) => [
                      t.code ?? "—",
                      <Link to={`/tasks/${t.id}`} className="hover:underline">{t.title}</Link>,
                      t.status ?? "—",
                      formatDate(t.dueAt),
                      t.assigneeName ?? "—",
                    ])}
                  />
                )}
                {related && related.actions.length > 0 && (
                  <RelatedSection
                    title="Actions"
                    columns={["#", "Title", "Status", "Due Date", "Assignee"]}
                    rows={related.actions.map((a) => [
                      a.code ?? "—",
                      <Link to={`/actions/${a.id}`} className="hover:underline">{a.title}</Link>,
                      a.status ?? "—",
                      formatDate(a.dueAt),
                      a.assigneeName ?? "—",
                    ])}
                  />
                )}
                {related && related.meetings.length > 0 && (
                  <RelatedSection
                    title="Meetings"
                    columns={["#", "Title", "Date", "Status"]}
                    rows={related.meetings.map((m) => [
                      m.code ?? "—",
                      <Link to={`/meetings/${m.id}`} className="hover:underline">{m.title}</Link>,
                      formatDate(m.date),
                      m.status ?? "—",
                    ])}
                  />
                )}
              </div>
            )}

            {tab === "comments" && (
              <div className="space-y-4">
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {comments.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No comments yet. Start the discussion.
                    </div>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="flex gap-3">
                        <div
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[hsl(210,55%,88%)] text-[11px] font-semibold text-[hsl(210,100%,25%)]"
                        >
                          {(c.authorName ?? "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-[12px]">
                            <span className="font-medium">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-muted-foreground">
                              {formatRelative(c.createdAt)}
                            </span>
                            {user?.id === c.authorId && (
                              <button
                                type="button"
                                onClick={() => void deleteComment.mutateAsync(c.id)}
                                className="ml-auto text-muted-foreground hover:text-destructive"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[13px]">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div
                  className="flex gap-2 border-t pt-4"
                  style={{ borderColor: BORDER }}
                >
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        void handleAddComment();
                      }
                    }}
                    placeholder="Write a comment... (Ctrl+Enter to submit)"
                    className="flex-1 resize-none rounded-md border px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
                    style={{ borderColor: BORDER, minHeight: 60 }}
                  />
                  <Button
                    onClick={() => void handleAddComment()}
                    disabled={!commentText.trim() || addComment.isPending}
                  >
                    <Send className="h-4 w-4" strokeWidth={1.5} />
                    Send
                  </Button>
                </div>
              </div>
            )}

            {tab === "attachments" && (
              <FileUploadZone
                entityType="note"
                entityId={note.id}
                entityLabel={note.code ?? note.title}
                noteId={note.id}
                companyId={note.companyId ?? undefined}
                productId={note.productId ?? undefined}
                projectId={note.projectId ?? undefined}
                meetingId={note.meetingId ?? undefined}
                taskId={note.taskId ?? undefined}
                actionId={note.actionId ?? undefined}
                showExisting
              />
            )}
          </div>
        </div>

        {/* ROW 4 — Metadata */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER }}
        >
          <button
            type="button"
            onClick={() => setMetaExpanded((v) => !v)}
            className="flex w-full items-center justify-between px-5 py-3 text-left"
          >
            <span className="text-[13px] font-semibold">Note Details</span>
            {metaExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            )}
          </button>
          {metaExpanded && (
            <div
              className="space-y-4 border-t p-5"
              style={{ borderColor: BORDER }}
            >
              <div
                className="grid grid-cols-4 gap-4 text-[13px]"
              >
                <Field label="Created By" value={note.createdByName ?? "—"} />
                <Field label="Created At" value={new Date(note.createdAt).toLocaleString()} />
                <Field label="Updated At" value={new Date(note.updatedAt).toLocaleString()} />
                <Field label="Word Count" value={String(note.wordCount ?? wordCount)} />
                <Field label="Type" value={typeLabel} />
                <Field label="Status" value={statusLabel} />
                <Field label="Visibility" value={note.visibility === "private" ? "Private" : "Team"} />
                <Field label="Code" value={note.code ?? "—"} />
              </div>
              <div className="border-t pt-4" style={{ borderColor: BORDER }}>
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Linked Context
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-[13px]">
                  <LinkedRow label="Company" value={note.companyName} href={note.companyId ? `/companies/${note.companyId}` : null} />
                  <LinkedRow label="Product" value={note.productName} href={note.productId ? `/products/${note.productId}` : null} />
                  <LinkedRow label="Project" value={note.projectName} href={note.projectId ? `/projects/${note.projectId}` : null} />
                  <LinkedRow label="Phase" value={note.phaseName} />
                  <LinkedRow label="Stage" value={note.stageName} />
                  <LinkedRow
                    label="Meeting"
                    value={note.meetingTitle ? `${note.meetingCode ?? ""} ${note.meetingTitle}`.trim() : null}
                    href={note.meetingId ? `/meetings/${note.meetingId}` : null}
                  />
                  <LinkedRow
                    label="Task"
                    value={note.taskTitle ? `${note.taskCode ?? ""} ${note.taskTitle}`.trim() : null}
                    href={note.taskId ? `/tasks/${note.taskId}` : null}
                  />
                  <LinkedRow
                    label="Action"
                    value={note.actionTitle ? `${note.actionCode ?? ""} ${note.actionTitle}`.trim() : null}
                    href={note.actionId ? `/actions/${note.actionId}` : null}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete note?"
        description={note ? `Are you sure you want to delete '${note.title}'? It will be removed from your Notes list.` : ""}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-[13px] text-foreground">{value}</div>
    </div>
  );
}

function LinkedRow({ label, value, href }: { label: string; value: string | null; href?: string | null }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">{value}</Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}

function RelatedSection({
  title,
  columns,
  rows,
}: {
  title: string;
  columns: string[];
  rows: Array<Array<React.ReactNode>>;
}) {
  return (
    <div>
      <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="overflow-hidden rounded-lg border" style={{ borderColor: BORDER }}>
        <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
          <thead className="bg-[hsl(210,30%,97%)] text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <tr>
              {columns.map((c) => (
                <th key={c} className="px-3 py-2">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-2">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
